<script setup lang="ts">
import { chartColors } from '@forgd/ui-kit/charts/lib/colors'
import type { DistributionGroup } from '@forgd/contract/types'
import { formatValue } from '@forgd/formatter'
import { Alert, AlertDescription } from '@forgd/ui-kit/alert'
import { ColorKey, ColorKeyLabel, ColorKeyValue } from '@forgd/ui-kit/color-key'
import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/vue/24/outline'
import { Donut } from '@unovis/ts'
import { VisDonut, VisSingleContainer } from '@unovis/vue'

const props = withDefaults(defineProps<{
  groups: Array<DistributionGroup>
  totalAllocation: number
  groupBy: DistributionGroupByType
  hideAllocationAlerts?: boolean
  size?: number
  centered?: boolean
}>(), {
  hideAllocationAlerts: false,
  size: DEFAULT_SIZE,
})

const outerRadius = computed(() => props.size / 2)
const arcWidth = computed(() => Math.round(props.size * 52 / DEFAULT_SIZE))
const innerRadius = computed(() => Math.round(props.size * 90 / DEFAULT_SIZE))

const value = (d: DistributionGroup) => d.allocationPercentage

const attributes = {
  [Donut.selectors.background]: {
    class: 'donut-background',
  },
}

function getGroupedValues(
  field: keyof DistributionGroup,
  otherGroupName: string,
  top: number = 3,
) {
  if (!field) {
    throw new Error('Field must be provided')
  }

  const groupedValues = props.groups
    .filter(group => group.allocationPercentage && group.allocationPercentage > 0)
    .reduce((acc, group) => {
      const value = group[field] as unknown as string
      if (value) {
        if (!acc[value]) {
          acc[value] = 0
        }
        acc[value] += group.allocationPercentage
      }
      return acc
    }, {} as Record<string, number>)

  return Object.entries(groupedValues)
    .map(([name, allocationPercentage]) => ({ name, allocationPercentage }))
    .sort((a, b) => b.allocationPercentage - a.allocationPercentage)
    .reduce((acc, item, i) => {
      if (i < top) {
        acc.push(item)
      }
      else {
        if (!acc[top]) {
          acc[top] = {
            name: otherGroupName,
            allocationPercentage: 0,
            qty: 0,
          }
        }
        acc[top].allocationPercentage += (item.allocationPercentage || 0)
        acc[top].qty = (acc[top].qty || 0) + 1
      }
      return acc
    }, [] as Array<{ name: string, allocationPercentage: number, qty?: number }>)
}

const rollup = computed(() => {
  switch (props.groupBy) {
    case 'category':
      return getGroupedValues('category', 'Other categories')
    case 'int-ext': {
      const groupedValues = getGroupedValues('type', '')
      return groupedValues.map((g) => {
        return {
          name: g.name === 'internal' ? 'Internal groups' : 'External groups',
          allocationPercentage: g.allocationPercentage,
          qty: undefined,
        }
      })
    }
    case 'group':
    default:
      return getGroupedValues('name', 'Other groups')
  }
})

const chartData = computed(() => {
  return [...rollup.value, { allocationPercentage: 100 - props.totalAllocation }]
})

const colors = computed(() => {
  return rollup.value.map((items, idx) => {
    if (props.totalAllocation > 100) {
      return '#95958D'
    }
    else {
      return chartColors.distributionPreview[idx]
    }
  }).concat('#95958D')
})
</script>

<script lang="ts">
export type DistributionGroupByType = 'group' | 'category' | 'int-ext'

export const DEFAULT_SIZE = 212
</script>

<template>
  <div class="@container w-full">
    <div
      data-el="DtDistributionPreview"
      class="flex gap-10 @max-md:flex-col @max-md:items-center @max-md:gap-6"
      :class="centered ? 'justify-center' : 'justify-start'"
    >
      <div class="dual-single-container">
        <VisSingleContainer class="chart z-0" :width="size" :height="size">
          <VisDonut
            :value="value"
            :color="colors"
            :radius="outerRadius"
            :arc-width="arcWidth"
            :attributes="attributes"
            :show-background="true"
          />
        </VisSingleContainer>
        <VisSingleContainer class="chart z-10" :data="chartData" :width="size" :height="size">
          <VisDonut
            :value="value"
            :color="colors"
            :radius="innerRadius"
            :attributes="attributes"
          />
        </VisSingleContainer>
      </div>

      <div v-if="rollup.length" class="space-y-5 @max-md:grid @max-md:grid-cols-2 @max-md:space-y-0 @max-md:gap-x-6 @max-md:gap-y-4 @max-md:self-stretch">
        <ColorKey
          v-for="(group, index) in rollup"
          :key="group.name"
          :color="chartColors.distributionPreview[index] ?? ''"
        >
          <ColorKeyLabel>{{ group.qty !== undefined ? `${group.name} (${group.qty})` : group.name }}</ColorKeyLabel>
          <ColorKeyValue class="text-base">
            {{ formatValue(group.allocationPercentage, 'percent:2') }}
          </ColorKeyValue>
        </ColorKey>
      </div>
    </div>
  </div>
  <template v-if="!hideAllocationAlerts">
    <Alert v-if="totalAllocation === 100" variant="success" size="sm">
      <CheckCircleIcon aria-hidden="true" />
      <AlertDescription>Great work! You have customized this configuration. Complete your groups' distribution and can mark this step as complete.</AlertDescription>
    </Alert>

    <Alert v-if="totalAllocation < 100 && totalAllocation !== 0" variant="info" size="sm">
      <InformationCircleIcon aria-hidden="true" />
      <AlertDescription>You still have a percentage of the distribution pending to be allocated. Ensure all your groups add up to 100%</AlertDescription>
    </Alert>

    <Alert v-if="totalAllocation > 100" variant="warning" size="sm">
      <ExclamationTriangleIcon aria-hidden="true" />
      <AlertDescription>The total quantity of tokens allocated exceeds 100% of the indicated total token supply. Please review the values before continuing to the next step.</AlertDescription>
    </Alert>

    <Alert v-if="!rollup.length" variant="warning" size="sm">
      <ExclamationTriangleIcon aria-hidden="true" />
      <AlertDescription>This chart is not displaying the listed groups since the total percentage allocation exceeds 100%</AlertDescription>
    </Alert>
  </template>
</template>

<style scoped>
:deep(.donut-background) {
  fill: #E1E1DC;
  stroke: #C7C7BE;
}

.dual-single-container {
  display: grid;
  grid-template-columns: 1fr;
}

.dual-single-container .chart {
  grid-column-start: 1;
  grid-row-start: 1;
}
</style>
